import { GetServerSidePropsContext, NextPageContext } from 'next'

import type { ApiClient } from '@mondough/public-api-request'

import { supportedLanguagesByLocaleCode } from './locales'
import {
  imminentRegionsByAlpha2RegionCode,
  supportedRegionsByAlpha2RegionCode,
} from './regions'

type Languages = typeof supportedLanguagesByLocaleCode
export type LiveLanguageCodes = keyof Languages

type Regions = typeof supportedRegionsByAlpha2RegionCode
export type LiveRegionCodes = keyof Regions
export type LiveRegions = Regions[LiveRegionCodes]
export type LiveRegionCodesAlpha3 = Regions[LiveRegionCodes]['alpha3']

type ImminentRegionsMap = typeof imminentRegionsByAlpha2RegionCode
export type ImminentRegionCodes = keyof ImminentRegionsMap
export type ImminentRegions = ImminentRegionsMap[ImminentRegionCodes]

export type Locale = Languages[LiveLanguageCodes]

// Locale source preferences
export const Source = {
  /** From authenticated user API*/
  USER_PREFERENCE: { name: 'user_preference', precedence: 8 },
  /** From Next.js i18n router */
  NEXTJS_I18N: { name: 'nextjs_i18n', precedence: 4 },
  /** From Accept-Language header */
  ACCEPT_LANG_HEADER: { name: 'acceptlanguage_header', precedence: 2 },
  /** From navigator.language */
  BROWSER: { name: 'browser', precedence: 2 },
  /** Fallback */
  FALLBACK: { name: 'fallback', precedence: 1 },
  /** Manual override */
  MANUAL_OVERRIDE: { name: 'manual_override', precedence: 0 },
  /** App override for when a consuming application chooses locale based on some other logic */
  APP_OVERRIDE: { name: 'app_override', precedence: 0 },
} as const

type LocaleSource = (typeof Source)[keyof typeof Source]

export type ResolvedLocale = {
  localeCode: LiveLanguageCodes
  source: LocaleSource
  fromCookie?: boolean
}

type LiveRegionAlpha3Codes = Regions[LiveRegionCodes]['alpha3']

export const liveRegionAlpha3Codes = Object.values(
  supportedRegionsByAlpha2RegionCode,
).map((region) => region.alpha3)

export type UserI18nPreferences = {
  user_id: string
  preferences: Record<
    LiveRegionAlpha3Codes,
    {
      available_languages_for_country: Array<{ language: LiveLanguageCodes }>
      available_languages_for_user: Array<LiveLanguageCodes>
      default_used: boolean
      language: LiveLanguageCodes
      other_languages_available_for_country: boolean
    }
  >
}

// Configuration options provided when initializing the library
type ReadLocaleOptions = {
  /**
   * default value: false
   * ignore the cookie which may contain a locale, but leave it untouched
   */
  ignoreCookie?: boolean
  /**
   * default value: true
   * write the latest value that was resolved to the cookie
   */
  persist?: boolean
}

/** Public interface of the locale detection library */
export interface LocaleManager {
  /**
   * Returns a list of all currently supported locales
   */
  getSupportedLocales(): Locale[]

  /**
   * Given a locale code (lang-REGION), returns a full locale object
   * with all relevant metadata (e.g. flag emoji, full name etc)
   */
  getLocale(localeCode: LiveLanguageCodes): Locale

  /**
   * For server-side use. Takes in Next.js context, which may contain
   * i18n routing data. Can be used in either getServerSideProps or
   * getInitialProps. Will use locale from cookie if it exists, and
   * create the cookie if not.
   */
  fromRequestServerSide(
    ctx: GetServerSidePropsContext | NextPageContext,
    options?: ReadLocaleOptions,
  ): ResolvedLocale

  /**
   * For client-side use. Uses the navigator.language value, unless
   * there is an equal or higher precedence locale already in a cookie.
   * Only if the browser locale is highest precedence will it be used
   * and written to the cookie.
   */
  fromBrowser(options?: ReadLocaleOptions): ResolvedLocale

  /**
   * The highest-precedence locale value for when you have a user context
   * and can call s.api.i18n-preferences. This method will make an API
   * request and handle the response, giving you back a locale for the
   * user and relevant country (e.g. account country). This method will
   * *not* interact with the cookie in any way (either reading or writing).
   * N.B. using this requires your web app to have the appropriate scope
   * for i18n-preferences in s.platform.authorization
   */
  fromUserPreferencesClientSide(
    apiClient: ApiClient,
    country: string,
  ): Promise<ResolvedLocale>

  /**
   * For apps that resolve locale externally (e.g. monzo.com has its
   * own locale system). Writes the __monzo_locale_v1__ cookie with the
   * given locale code so it persists across subdomains for downstream
   * apps (business.monzo.com, auth.monzo.com, etc.).
   *
   * Works in both server and client contexts:
   * - Server-side: appends to Set-Cookie response headers
   * - Client-side: writes to document.cookie (for client route transitions)
   *
   * Normalises locale codes to ll-RR format and silently no-ops if
   * the locale code is not supported.
   */
  persistExternalLocale(
    ctx: GetServerSidePropsContext | NextPageContext,
    localeCode: string,
  ): void
}
